import React from "react";
import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import moment from "moment";
import NumberFormat from "react-number-format";
import { DoneIcon } from "./StepIcons";
import TransactionHistoryBase from "./TransactionHistoryBase";
import styles from "./styles";
import {
  getCompletedRelated,
  getRefundText,
  getSuccessText,
  getRefundResult,
  isCorrectionsExist,
} from "./utils";

const SwitchTrx = ({
  status,
  method,
  amount,
  unit,
  fee,
  submittedDate,
  processingDate,
  completedDate,
  details,
  isSwitchIn,
  isSwitchOut,
  isSwitchAll,
  salesChargeAmount,
  salesChargeCcy,
  salesChargePercent,
  planName,
  transactionType,
  isManSwitchAll,
  isManSwitchIn,
  isManSwitchOut
}) => {
  const completed = getCompletedRelated({
    status,
    isDoneIconActive: status === "5",
  });
  const middleText = isSwitchIn ? "In" : "Out";

  const isSubmitted =
    status === "3" ||
    status === "4" ||
    status === "5" ||
    status === "6" ||
    status === "9" ||
    status === "10" ||
    status === "11";

  const isProcessing =
    status === "4" ||
    status === "5" ||
    status === "9" ||
    status === "10" ||
    status === "11";

  const isCompleted =
    status === "5" || status === "9" || status === "10" || status === "11";

  const isPendingRefund = status === "9";
  const restRefundText = (text, notSwitchIn) => {
    if (notSwitchIn) {
      return ".";
    }
    if (text === "Switch Out amount have refunded to") {
      return " and has been reflected in your account.";
    }
    if (text === "Switch In amount have refunded to") {
      return " and has been reflected in your account.";
    }
    return ".";
  };

  const detail =
    Array.isArray(details) &&
    details.length > 0 &&
    details.find(item => item.type === transactionType);

  const { serviceTaxAmount = 0, serviceTaxRate = 0 } = detail || {};

  return (
    <TransactionHistoryBase manualAdjustment={null}>
      {/* Submitted */}
      <Step active={isSubmitted}>
        <StepLabel
          icon={<DoneIcon active={isSubmitted} />}
          sx={{ padding: "0" }}
        >
          <Typography
            color="textPrimary"
            style={{ ...styles.baseFont, ...styles.label }}
          >
            Submitted
          </Typography>
        </StepLabel>
        <StepContent
          style={{
            borderColor: isSubmitted && "#0091DA",
            borderWidth: 2,
            marginTop: "0",
            height: "100%",
            paddingBottom: "24px",
          }}
        >
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              ...styles.date,
              marginTop: "8px",
            }}
          >
            {moment(submittedDate)
              .utcOffset("+0800")
              .format(
                isManSwitchAll || isManSwitchIn || isManSwitchOut
                  ? "DD MMM YYYY"
                  : "DD MMM YYYY, h:mm a"
              )}
          </Typography>
        </StepContent>
      </Step>

      {/* Processing */}
      <Step active={isProcessing}>
        <StepLabel
          icon={<DoneIcon active={isProcessing} />}
          sx={{ padding: "0" }}
        >
          <Typography
            color="textPrimary"
            style={{
              ...styles.baseFont,
              ...styles.label,
              color: isProcessing ? "#000000" : "#9D9EA4",
            }}
          >
            Processing
          </Typography>
        </StepLabel>
        <StepContent
          style={{
            borderColor: isProcessing && "#0091DA",
            borderWidth: 2,
            height: "100%",
            marginTop: "0",
            paddingBottom: "24px",
          }}
        >
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              ...styles.date,
              marginTop: "8px",
            }}
          >
            {moment(processingDate)
              .utcOffset("+0800")
              .format(
                isManSwitchAll || isManSwitchIn || isManSwitchOut
                  ? "DD MMM YYYY"
                  : "DD MMM YYYY, h:mm a"
              )}
          </Typography>
        </StepContent>
      </Step>

      {/* Completed */}
      <Step active={isCompleted}>
        <StepLabel icon={completed.icon} sx={{ padding: "0" }}>
          <Typography
            color="textPrimary"
            style={{
              ...styles.baseFont,
              ...styles.label,
              color:
                status === "5" || status === "9" || status === "10"
                  ? "#000000"
                  : "#9D9EA4",
            }}
          >
            {completed.text}
          </Typography>
        </StepLabel>
        <StepContent
          style={{
            borderColor:
              (getRefundResult(details, status, isSwitchIn) ||
                detail?.corrections?.length > 0) &&
              "#0091DA",
            borderWidth: 2,
            marginTop: "0",
            height: "100%",
            marginBottom:
              (status === "9" ||
                (Array.isArray(detail?.corrections) &&
                  details.corrections?.length > 0)) &&
              "24px",
          }}
        >
          {(isSwitchOut ||
            isSwitchAll ||
            isManSwitchAll ||
            (isSwitchIn && status === "9")) && (
            <Typography style={{ ...styles.baseFont, ...styles.content }}>
              {getSuccessText(
                status,
                middleText,
                (
                  details?.find(
                    f => f.type === "SwitchIn" || f.type === "ManSwitchIn"
                  ) || {}
                )?.planAlias
              )}
            </Typography>
          )}

          {isSwitchIn && status !== "9" && (
            <>
              <div
                style={{
                  marginTop: "3px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  Initial Switch In:
                </Typography>
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  <NumberFormat
                    prefix="RM"
                    value={amount}
                    thousandSeparator
                    displayType="text"
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </Typography>
              </div>
              {salesChargePercent > 0 && (
                <div
                  style={{
                    marginTop: "3px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                    }}
                  >
                    Sales Charge ({salesChargePercent || 0}%)
                  </Typography>
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                    }}
                  >
                    <NumberFormat
                      prefix="RM"
                      value={salesChargeAmount || 0}
                      thousandSeparator
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                    />
                  </Typography>
                </div>
              )}
              {/* START: SST */}
              <div
                style={{
                  marginTop: "3px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  SST ({serviceTaxRate || 0}%)
                </Typography>
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  <NumberFormat
                    prefix="RM"
                    value={serviceTaxAmount || 0}
                    thousandSeparator
                    displayType="text"
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </Typography>
              </div>
              {/* END: SST */}

              <div
                style={{
                  marginTop: "3px",
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "700",
                  color: "#616367",
                }}
              >
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                    fontWeight: "inherit",
                    color: "inherit",
                  }}
                >
                  Net Switch In:
                </Typography>
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                    fontWeight: "inherit",
                    color: "inherit",
                  }}
                >
                  <NumberFormat
                    prefix="RM"
                    value={amount - salesChargeAmount}
                    thousandSeparator
                    displayType="text"
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </Typography>
              </div>
            </>
          )}

          {status !== "9" && (
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography style={{ ...styles.baseFont, ...styles.content }}>
                Unit: {unit}
              </Typography>
              <Typography style={{ ...styles.baseFont, ...styles.content }}>
                Unit Price:{" "}
                <NumberFormat
                  prefix="RM"
                  value={detail?.actualUnitPrice || parseFloat(amount / unit)}
                  thousandSeparator
                  displayType="text"
                  decimalScale={4}
                  fixedDecimalScale
                />
              </Typography>
            </div>
          )}

          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              ...styles.date,
              marginTop: "8px",
            }}
          >
            {moment(completedDate)
              .utcOffset("+0800")
              .format("DD MMM YYYY")}
          </Typography>
        </StepContent>
      </Step>
      {/* refund */}
      {status === "9" && (
        <Step active={isProcessing}>
          <StepLabel
            icon={
              <DoneIcon active={getRefundResult(details, status, isSwitchIn)} />
            }
            sx={{ padding: "0" }}
          >
            <Typography
              color="textPrimary"
              style={{
                ...styles.baseFont,
                ...styles.label,
                color: isPendingRefund ? "#000000" : "#9D9EA4",
              }}
            >
              {getRefundResult(details, status, isSwitchIn)
                ? "Refund Completed"
                : "Pending Refund"}
            </Typography>
          </StepLabel>
          <StepContent
            style={{
              borderColor: isProcessing && "#0091DA",
              borderWidth: 2,
              height: "100%",
              marginTop: "0",
            }}
          >
            <Typography style={{ ...styles.baseFont, ...styles.content }}>
              {`${getRefundText(isSwitchIn, details)}`}{" "}
              <span style={{ fontWeight: "700" }}>{planName}</span>
              {restRefundText(getRefundText(isSwitchIn, details), isSwitchIn)}
            </Typography>

            {getRefundResult(details, status, isSwitchIn) && (
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                  ...styles.date,
                  marginTop: "8px",
                }}
              >
                {moment(completedDate)
                  .utcOffset("+0800")
                  .format("DD MMM YYYY")}
              </Typography>
            )}
          </StepContent>
        </Step>
      )}
      {Array.isArray(detail?.corrections) &&
        detail?.corrections.map((item, id) => (
          <React.Fragment key={id}>
            {item.reversal && (
              <Step active>
                <StepLabel icon={<DoneIcon active />} sx={{ padding: "0" }}>
                  <Typography
                    color="textPrimary"
                    style={{
                      ...styles.baseFont,
                      ...styles.label,
                      color: "#000000",
                    }}
                  >
                    Reverse Switch {middleText}
                  </Typography>
                </StepLabel>
                <StepContent
                  style={
                    item.hasOwnProperty("correction")
                      ? {
                          borderColor: "#0091DA",
                          borderWidth: 2,
                          marginTop: "0",
                          height: "100%",
                          paddingBottom: "24px",
                        }
                      : { border: "none" }
                  }
                >
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      ...styles.date,
                      marginTop: "8px",
                    }}
                  >
                    {moment(item.reversal.navDate)
                      .utcOffset("+0800")
                      .format("DD MMM YYYY")}
                  </Typography>
                </StepContent>
              </Step>
            )}
            {item.correction && (
              <Step active>
                <StepLabel icon={<DoneIcon active />} sx={{ padding: "0" }}>
                  <Typography
                    color="textPrimary"
                    style={{
                      ...styles.baseFont,
                      ...styles.label,
                      color: "#000000",
                    }}
                  >
                    Adjusted Switch {middleText}
                  </Typography>
                </StepLabel>
                <StepContent
                  style={
                    id !== detail?.corrections.length - 1
                      ? {
                          borderColor: "#0091DA",
                          borderWidth: "2px",
                          height: "100%",
                          marginTop: "0",
                          paddingBottom: "24px",
                        }
                      : { border: "none" }
                  }
                >
                  {detail.fundCode !==
                    item?.correction[(isSwitchIn ? "fundCodeTo" : "fundCode")]
                    && (
                    <Typography
                      style={{
                        marginTop: "3px",
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      {
                        item?.correction[
                          (isSwitchIn ? "fundNameTo" : "fundName")
                        ]
                      }
                    </Typography>
                  )}
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      Request Switch {middleText}
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      <NumberFormat
                        prefix="RM"
                        value={amount}
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      Adjusted Switch {middleText}
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      <NumberFormat
                        prefix="RM"
                        value={item.correction.actualAmt}
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>

                  {isSwitchIn && (
                    <>
                      {item.correction?.salesChargePercent > 0 && (
                        <div
                          style={{
                            marginTop: "3px",
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            style={{
                              ...styles.baseFont,
                              ...styles.content,
                            }}
                          >
                            Sales Charge (
                            {item.correction.salesChargePercent || 0}%)
                          </Typography>
                          <Typography
                            style={{
                              ...styles.baseFont,
                              ...styles.content,
                            }}
                          >
                            <NumberFormat
                              prefix="RM"
                              value={item.correction.salesChargeAmount || 0}
                              thousandSeparator
                              displayType="text"
                              decimalScale={2}
                              fixedDecimalScale
                            />
                          </Typography>
                        </div>
                      )}

                      {/* START: SST */}
                      <div
                        style={{
                          marginTop: "3px",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          style={{
                            ...styles.baseFont,
                            ...styles.content,
                          }}
                        >
                          SST ({item.correction.serviceTaxRate || 0}%)
                        </Typography>
                        <Typography
                          style={{
                            ...styles.baseFont,
                            ...styles.content,
                          }}
                        >
                          <NumberFormat
                            prefix="RM"
                            value={item.correction.serviceTaxAmount || 0}
                            thousandSeparator
                            displayType="text"
                            decimalScale={2}
                            fixedDecimalScale
                          />
                        </Typography>
                      </div>
                      {/* END: SST */}

                      <div
                        style={{
                          marginTop: "3px",
                          display: "flex",
                          justifyContent: "space-between",
                          fontWeight: "700",
                          color: "#616367",
                        }}
                      >
                        <Typography
                          style={{
                            ...styles.baseFont,
                            ...styles.content,
                            fontWeight: "inherit",
                            color: "inherit",
                          }}
                        >
                          Net Switch In:
                        </Typography>
                        <Typography
                          style={{
                            ...styles.baseFont,
                            ...styles.content,
                            fontWeight: "inherit",
                            color: "inherit",
                          }}
                        >
                          <NumberFormat
                            prefix="RM"
                            value={
                              item.correction?.actualAmt -
                              item.correction?.salesChargeAmount
                            }
                            thousandSeparator
                            displayType="text"
                            decimalScale={2}
                            fixedDecimalScale
                          />
                        </Typography>
                      </div>
                    </>
                  )}

                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{ ...styles.baseFont, ...styles.content }}
                    >
                      Unit: {item.correction.actualUnit}
                    </Typography>
                    <Typography
                      style={{ ...styles.baseFont, ...styles.content }}
                    >
                      Unit Price:{" "}
                      <NumberFormat
                        prefix="RM"
                        value={
                          item.correction.actualUnitPrice ||
                          parseFloat(
                            item.correction.amount / item.correction.unit
                          )
                        }
                        thousandSeparator
                        displayType="text"
                        decimalScale={4}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      ...styles.date,
                    }}
                  >
                    {moment(item.correction.navDate)
                      .utcOffset("+0800")
                      .format("DD MMM YYYY")}
                  </Typography>
                </StepContent>
              </Step>
            )}
          </React.Fragment>
        ))}
    </TransactionHistoryBase>
  );
};

export default SwitchTrx;
